export interface Programme {
  slug: string;
  title: string;
  description: string;
  duration: string;
  audience: string;
  heroHeadline: string;
  heroSubheadline: string;
  problemBullets: string[];
  outcomeBullets: string[];
  features: string[];
  steps: { title: string; description: string }[];
  faqs: { question: string; answer: string }[];
}

export const programmes: Programme[] = [
  {
    slug: "executive-mentorship",
    title: "1-to-1 Executive Mentorship",
    description: "A private 1-to-1 mentorship designed to help you understand AI, create professional content, build websites and apps with AI tools, and unlock digital income opportunities.",
    duration: "6 Weeks",
    audience: "Entrepreneurs, business owners, content creators, students and professionals",
    heroHeadline: "Master AI. Build Digital Income. Thrive in the Future Economy.",
    heroSubheadline: "A six-week private mentorship for learning practical AI and digital creation skills with direct guidance from Tunde Adedokun.",
    problemBullets: [
      "Feeling overwhelmed by the sheer volume of AI tools and not knowing where to start?",
      "Watching others build digital income while you remain stuck on the sidelines?",
      "Struggling to find a credible, human mentor who understands your specific context and goals?"
    ],
    outcomeBullets: [
      "Build a personal prompt library for ongoing use.",
      "Create AI-assisted content and an edited video project for your portfolio.",
      "Build a simple AI-powered website or web application and explore automation workflows."
    ],
    features: [
      "6 Weeks Duration",
      "2 Days Per Week",
      "2hrs Per Session",
      "24hrs Total Training"
    ],
    steps: [
      { title: "Understand", description: "Learn how large language models work and where they can support your goals." },
      { title: "Prompt", description: "Practise professional prompt-engineering techniques for more useful AI outputs." },
      { title: "Create", description: "Use AI for content, graphics, video editing and digital assets." },
      { title: "Build", description: "Apply your learning to a website, web application and practical automation workflows." }
    ],
    faqs: [
      { question: "Who is this mentorship for?", answer: "It is for entrepreneurs, business owners, content creators, students and professionals who want practical, future-focused AI skills." },
      { question: "Do I need advanced technical skills?", answer: "No advanced technical background is required. The training begins with AI foundations and moves into practical creation and building." },
      { question: "What is the time commitment?", answer: "The mentorship runs for six weeks, with two two-hour sessions each week and 24 hours of training in total." },
      { question: "What happens after I book a consultation?", answer: "Choose an available time in the booking calendar so we can discuss your goals and answer your questions." }
    ]
  },
  {
    slug: "teen-ai-builders-academy",
    title: "Teen AI Builders Academy",
    description: "A safety-first programme where young builders learn prompt engineering, creative AI, app building and automation before presenting a capstone product.",
    duration: "12 Weeks (Sundays)",
    audience: "Young people (Ages 10-16)",
    heroHeadline: "Help Your Teen Build With AI.",
    heroSubheadline: "A 12-week Sunday programme for young builders aged 10–16, with five practical modules and parents involved throughout.",
    problemBullets: [
      "Worried your teenager is consuming technology rather than creating with it?",
      "Unsure how to guide them in using AI safely and productively for their education?",
      "Want to give them a head start in the skills that will define their future career?"
    ],
    outcomeBullets: [
      "Create original AI-assisted image, video and audio work.",
      "Build a live landing page and a working application.",
      "Complete a capstone product and present it at Demo Day."
    ],
    features: [
      "12 Weeks Duration",
      "2hrs Per Sunday Session",
      "5 Practical Modules",
      "1 Capstone Product"
    ],
    steps: [
      { title: "Foundations", description: "Explore the AI landscape, prompt engineering and a personal AI toolkit." },
      { title: "Create", description: "Work hands-on with image, video, audio and research AI models." },
      { title: "Build", description: "Create websites, full-stack apps, automation workflows and AI agents." },
      { title: "Productise & Launch", description: "Develop digital products before the capstone build sprint and Demo Day." }
    ],
    faqs: [
      { question: "Who is the Academy for?", answer: "The Academy is for young people aged 10–16. The programme begins with foundations, so prior AI experience is not required." },
      { question: "What is the schedule?", answer: "The programme runs for 12 weeks, with a two-hour session each Sunday." },
      { question: "What will participants build?", answer: "Participants work across five modules and finish by building and presenting a capstone product." },
      { question: "How are families involved?", answer: "The programme uses a family-involved, safety-first model, with a parent on file and safety guidance built into the learning journey." }
    ]
  },
  {
    slug: "group-training",
    title: "AI & Digital Skills Acquisition Group Training",
    description: "Practical, hands-on group training in AI foundations, digital creation, websites, apps and automation.",
    duration: "Flexible Intensive",
    audience: "Beginners, creators, entrepreneurs, business owners, students and professionals",
    heroHeadline: "Build, Create and Learn With AI.",
    heroSubheadline: "Structured live classes and hands-on group work for people who want practical AI and digital creation skills.",
    problemBullets: [
      "Want to learn AI but prefer learning in a collaborative, group setting?",
      "Need a structured way to move from AI foundations into practical creation?",
      "Want hands-on experience with content, websites, apps and automation?"
    ],
    outcomeBullets: [
      "Practise AI foundations and professional prompting in structured classes.",
      "Create AI-assisted images, video, audio and design assets.",
      "Explore websites, simple apps and automation through hands-on group work."
    ],
    features: [
      "Flexible Intensive",
      "Structured Classes",
      "Live + Hands-on",
      "Group Experience"
    ],
    steps: [
      { title: "AI Foundations", description: "Understand large language models and how tools such as ChatGPT and Claude can be used effectively." },
      { title: "Prompt & Create", description: "Practise prompt engineering and AI-assisted content creation." },
      { title: "Design & Edit", description: "Work with AI media tools, CapCut and Canva AI." },
      { title: "Build & Automate", description: "Explore websites, simple apps and practical automation workflows." }
    ],
    faqs: [
      { question: "Who is the group training for?", answer: "It is for beginners, creators, entrepreneurs, business owners, students and professionals who want practical AI and digital skills." },
      { question: "What is the format?", answer: "The programme uses structured live classes with hands-on group activities." },
      { question: "Do I need advanced technical skills?", answer: "No advanced technical background is required. The programme starts with AI foundations." },
      { question: "When does the next programme begin?", answer: "Book a free consultation to ask about current group-training dates." }
    ]
  }
];
